import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/app/store/authStore';
import { MobileInput } from '@/components/ui/MobileInput';
import { MobileButton } from '@/components/ui/MobileButton';
import { Shield } from 'lucide-react';

const loginSchema = z.object({
    username: z.string().min(1, "Username is required"),
    password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
    const navigate = useNavigate();
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const loginAction = useAuthStore((state) => state.loginAction);
    const isLoading = useAuthStore((state) => state.isLoading);
    const error = useAuthStore((state) => state.error);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            username: "",
            password: ""
        }
    });

    const onSubmit = async (data: LoginFormValues): Promise<void> => {
        await loginAction(data);
    };

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/leads', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    return (
        <div className="min-h-screen bg-background flex flex-col pt-safe pb-safe">
            {/* Top decorative section */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                {/* Logo/Icon */}
                <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                    <Shield className="w-10 h-10 text-primary" />
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold text-foreground mb-2">
                    OTP Approver
                </h1>
                <p className="text-muted-foreground text-center mb-8">
                    Sign in to manage OTP approvals
                </p>

                {/* Login Form */}
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="w-full max-w-sm space-y-4"
                >
                    <MobileInput
                        label="Username"
                        placeholder="Enter your username"
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        error={errors.username?.message}
                        {...register("username")}
                    />

                    <MobileInput
                        label="Password"
                        type="password"
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        error={errors.password?.message}
                        {...register("password")}
                    />

                    {error && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3">
                            <p className="text-sm text-destructive">{error}</p>
                        </div>
                    )}

                    <MobileButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        isLoading={isLoading}
                        className="w-full mt-6"
                    >
                        {isLoading ? 'Signing in...' : 'Sign In'}
                    </MobileButton>
                </form>
            </div>

            {/* Bottom branding */}
            <div className="py-6 text-center">
                <p className="text-xs text-muted-foreground">
                    Manager Portal
                </p>
            </div>
        </div>
    );
}
